import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as dayjs from 'dayjs';
import {BehaviorSubject, forkJoin, Observable, throwError} from 'rxjs';
import {catchError, map, mergeMap, shareReplay} from 'rxjs/operators';
import formatHolidayCategory from '../../../../../helpers/format-holiday-category';
import fixArray from '../../../../../utils/fixArray';
import groupBy from '../../../../../utils/groupBy';
import minBy from '../../../../../utils/minBy';
import {MappingItem} from '../../../../shared/interfaces/mapping-item';
import {Occupancy} from '../../../../shared/interfaces/occupancy';
import {AccomodationSpecials} from '../../../../shared/models/accomodation-specials.enum';
import {VideoTypes} from '../../../../shared/models/video-types.enum';
import {BookingService} from '../../../../shared/services/booking.service';
import {EnvironmentService} from '../../../../shared/services/environment.service';
import {ProjectService} from '../../../../shared/services/project.service';
import {IHolidayDetail} from '../interfaces/iholiday-detail';
import {IHolidayDetailAccomodation} from '../interfaces/iholiday-detail-accomodation';
import {IHolidayDetailAccomodationRoom} from '../interfaces/iholiday-detail-accomodation-room';
import {IHolidayDetailFlight} from '../interfaces/iholiday-detail-flight';
import {IHolidayDetailOperative} from '../interfaces/iholiday-detail-operative';
import {IHolidayDetailPacket} from '../interfaces/iholiday-detail-packet';
import {IHolidayDetailParams} from '../interfaces/iholiday-detail-params';
import {IHolidayDetailPlace} from '../interfaces/iholiday-detail-place';
import {IHolidayDetailRoom} from '../interfaces/iholiday-detail-room';
import {IHolidayDetailSolution} from '../interfaces/iholiday-detail-solution';
import {IHolidayDetailTag} from '../interfaces/iholiday-detail-tag';
import {IHolidayDetailText} from '../interfaces/iholiday-detail-text';
import {IHolidayDetailTour} from '../interfaces/iholiday-detail-tour';
import {IHolidayDetailTourItinerary} from '../interfaces/iholiday-detail-tour-itinerary';
import {IHolidayDetailTourItineraryMedia} from '../interfaces/iholiday-detail-tour-itinerary-media';
import {IHolidayDetailTourItineraryStop} from '../interfaces/iholiday-detail-tour-itinerary-stop';
import {IHolidayDetailVideo} from '../interfaces/iholiday-detail-video';
import {OperativeTypology} from '../../../../shared/models/operative-typology.enum';
import {
  HolidayDetailGenericPageComponent,
  HolidayDetailGenericPageSearchParams
} from '../../holiday-detail-generic-page/holiday-detail-generic-page.component';


@Injectable({
  providedIn: 'root'
})
export class HolidayDetailService {

  occupancy: Occupancy = this._bookingService.occupancy;

  constructor(private _http: HttpClient,
              private _environmentService: EnvironmentService,
              private _projectService: ProjectService,
              private _bookingService: BookingService) {
  }

  httpCache: Map<string, Observable<any>> = new Map<string, Observable<any>>();

  formatPlace(place: any): IHolidayDetailPlace {
    return {id: place.Id, ll: place.Ll, lt: place.Lt, name: place.Name, parentId: place.ParentID};
  }

  formatText(text: any): IHolidayDetailText {
    return {
      id: text.id,
      title: text.title,
      order: text.progressive,
      description: text.description
    };
  }

  formatAccomodationRoom(room: any): IHolidayDetailAccomodationRoom {
    return {
      id: room.id,
      specials: room.roomSpecials ? fixArray(room.roomSpecials).map(e => this.formatSpecial(e)) : [],
      name: room.name,
      description: room.description,
      images: room.roomMedias ? fixArray(room.roomMedias).map(e => e.baseUrl + e.fileName) : undefined
    };
  }

  formatSpecial(special: any): IHolidayDetailTag {
    return {
      id: special.Id,
      name: special.Type,
      icon: special.Icon
    };
  }

  formatLanguage(language: any): IHolidayDetailTag {
    return {
      id: language.Id,
      name: language.Type,
      icon: `https://www.offertetouroperator.com/oto3/deployment/back/zeus-design-system/images/svg/languages/${language.Type}.svg`
    };
  }

  formatAccessory(accessory: any): IHolidayDetailTag {
    return {
      id: accessory.item.id,
      name: accessory.item.title,
      icon: accessory.item.icon
    };
  }

  formatAccomodation(accomodation: any): IHolidayDetailAccomodation {

    const specials = accomodation.AccommodationSpecial_Collection?.AccommodationSpecial;

    return {
      id: accomodation.Id,
      accommodationParentId: accomodation?.AccommodationParentId,
      name: accomodation.Name,
      tags: accomodation.AccommodationTag_Collection?.AccommodationTag
        ? fixArray(accomodation.AccommodationTag_Collection?.AccommodationTag).map(e => this.formatTag(e))
        : [],
      virtualTour: accomodation.VirtualTour,
      category: accomodation.Accomodation_Cat ?
        formatHolidayCategory(accomodation.Accomodation_Cat.Name, accomodation.Accomodation_Type.Type) : undefined,
      texts: fixArray(accomodation.Texts_Collection?.Text).filter(x => x).map(e => this.formatText(e)),
      activities: specials
        ? fixArray(specials).filter(e => e.Category === AccomodationSpecials.ACTIVITY).map(e => this.formatSpecial(e))
        : [],
      languages: specials
        ? fixArray(specials).filter(e => e.Category === AccomodationSpecials.LANGUAGE).map(e => this.formatLanguage(e))
        : [],
      positions: specials
        ? fixArray(specials).filter(e => e.Category === AccomodationSpecials.POSITION).map(e => this.formatSpecial(e))
        : [],
      facilities: specials
        ? fixArray(specials).filter(e => e.Category === AccomodationSpecials.FACILITY).map(e => this.formatSpecial(e))
        : [],
      images: fixArray(accomodation.Photos_Collection.Photo).filter(e => e).map(e => e.Url),
      lat: parseFloat(accomodation.Lt),
      lng: parseFloat(accomodation.Ll),
      rooms: accomodation.AccommodationRoom_Collection ?
        accomodation.AccommodationRoom_Collection.AccommodationRoom ?
          fixArray(accomodation.AccommodationRoom_Collection.AccommodationRoom).map(e => this.formatAccomodationRoom(e)) :
          undefined : undefined
    };
  }


  formatTourItineraryMedia(media: any): IHolidayDetailTourItineraryMedia {
    return {
      alt: media.title,
      id: media.id,
      type: media.type,
      url: media.baseUrl + media.fileName
    };
  }

  formatTourItineraryStop(stop: any, dayOfItinerary: number): IHolidayDetailTourItineraryStop {
    return {
      distance: stop.distance ? parseFloat(stop.distance.replace(',', '.')) : undefined,
      id: stop.id,
      order: stop.progressive,
      lat: parseFloat(stop.latitude),
      lng: parseFloat(stop.longitude),
      name: stop.destinationName,
      day: dayOfItinerary
    };
  }

  formatTourItinerary(itineraryStep: any): IHolidayDetailTourItinerary {
    return {
      breakfast: itineraryStep.breakfast === 1,
      day: itineraryStep.day,
      description: itineraryStep.description,
      dinner: itineraryStep.dinner === 1,
      id: itineraryStep.id,
      lunch: itineraryStep.lunch === 1,
      medias: itineraryStep.medias ? fixArray(itineraryStep.medias).map(e => this.formatTourItineraryMedia(e)) : [],
      stops: itineraryStep.stops ? fixArray(itineraryStep.stops).map(e => this.formatTourItineraryStop(e, itineraryStep.day)) : [],
      name: itineraryStep.title
    };
  }

  formatTag(tag: any): IHolidayDetailTag {
    return {
      id: tag.id || tag.Id,
      name: tag.description || tag.descripion,
      icon: tag.image
    };
  }

  formatTour(tour: any): IHolidayDetailTour {
    return {
      id: tour.id,
      images: tour.medias ? fixArray(tour.medias).filter(e => e.type.includes('image')).map(e => e.baseUrl + e.fileName) : [],
      texts: tour.details ? fixArray(tour.details).map(e => this.formatText(e)) : [],
      tags: tour.tags ? fixArray(tour.tags).map(e => this.formatTag(e)) : [],
      itinerary: tour.itinerary ? fixArray(tour.itinerary).map(e => this.formatTourItinerary(e)) : [],
      activities: [],
      facilities: [],
      languages: tour.languages ? fixArray(tour.languages).map(e => this.formatLanguage(e)) : []
    };
  }

  formatBasis(basis: any): MappingItem {
    return {
      id: basis.Id,
      name: basis.Name,
      code: basis.Code
    };
  }

  formatFlight(flight: any): IHolidayDetailFlight {
    return {
      arrivalAirport: flight.ArrivalAirport ? {
        name: flight.ArrivalAirport.Name,
        code: flight.ArrivalAirport.Code,
        id: flight.ArrivalAirport.Id
      } : undefined,
      arrivalDate: flight.ArrivalDate ? new Date(flight.ArrivalDate) : undefined,
      code: flight.Code,
      departureAirport: flight.DepartureAirport ? {
        name: flight.DepartureAirport.Name,
        code: flight.DepartureAirport.Code,
        id: flight.DepartureAirport.Id
      } : undefined,
      departureDate: flight.DepartureDate ? new Date(flight.DepartureDate) : undefined,
      id: flight.Id,
      type: flight.FlightType
    };
  }

  formatOperative(operative: any): IHolidayDetailOperative {
    const stopOverDuration = (operative.StopOverDuration) ? Number(operative.StopOverDuration) : undefined;
    let stepOverDurationString: string;
    if (stopOverDuration && stopOverDuration > 0) {
      stepOverDurationString = `${(Number(dayjs.duration(stopOverDuration, 'hours').format('H')) > 0) ? dayjs.duration(stopOverDuration, 'hours').format('H') + 'h ' : ''}${(Number(dayjs.duration(stopOverDuration, 'hours').format('m')) > 0) ? dayjs.duration(stopOverDuration, 'hours').format('m') + 'm' : ''}`;
    }

    return {
      arrivalPlace: operative.ArrivalPlace ? {
        name: operative.ArrivalPlace.Name,
        code: operative.ArrivalPlace.Code,
        id: operative.ArrivalPlace.Id
      } : undefined,
      arrivalDate: operative.ArrivalDate ? new Date(operative.ArrivalDate) : undefined,
      code: operative.Code,
      departurePlace: operative.DeparturePlace ? {
        name: operative.DeparturePlace.Name,
        code: operative.DeparturePlace.Code,
        id: operative.DeparturePlace.Id
      } : undefined,
      departureDate: operative.DepartureDate ? new Date(operative.DepartureDate) : undefined,
      stations: operative?.OperativeDepartureStations,
      name: operative?.Title,
      company: operative?.Company,
      id: operative.Id,
      type: operative.OperativeType,
      typology: (operative.OperativeTypology) ? Number(operative.OperativeTypology) : undefined,
      stopOver: (operative.StopOver) ? {
        name: operative.StopOver.Name,
        code: operative.StopOver.Code,
        id: operative.StopOver.Id
      } : undefined,
      stopOverDuration: stepOverDurationString || undefined,
    };
  }

  formatPacket(packet: any): IHolidayDetailPacket {
    return {
      description: packet.description,
      includes: packet.includes,
      id: packet.id,
      image: packet.imageBaseUrl + packet.image,
      tags: packet.tags ? fixArray(packet.tags).map(e => this.formatTag(e)) : [],
      title: packet.title,
      notesB2B: packet.notesB2B,
      notesB2C: packet.notesB2C
    };
  }

  formatRoom(room: any): IHolidayDetailRoom {
    return {
      adults: room.AdultsNum,
      availability: room.Availability,
      children: room.ChildsNum,
      infants: room.InfantsNum,
      bookingCode: room.BookingCode,
      price: parseFloat(room.MKPriceCP),
      basis: room.Basis ? fixArray(room.Basis).map(e => this.formatBasis(e))[0] : undefined,
      type: room.TypeExtended,
      source: room.Source,
      title: room.Description
    };
  }

  formatSolution(solution: any, holiday: any): IHolidayDetailSolution {
    let solutionIdOrSource;

    // aggiungo una proprietà per poter salvare l'id della solution o del source (in base a quella presente nella solution stessa)
    // in modo da poterla utilizzare evitando ulteriori controlli di nullità
    // NB: in alcune solution, appunto, l'Id potrebbe non essere presente e come "identificativo" viene considerato il Source
    if (solution.Id) {
      solutionIdOrSource = solution.Id;
    } else if (!solution.Id && solution.Source) {
      solutionIdOrSource = solution.Source;
    }

    const detailSolutionFormatted = {
      parentId: holiday.Id, parentSource: holiday.Source, parentType: holiday.Type, parentTop: holiday.TourOperatorID,
      basis: solution.Basis_Collection.Basis ? this.formatBasis(fixArray(solution.Basis_Collection.Basis)[0]) : undefined,
      days: solution.Days,
      airport: solution.DeparturePlaceId,
      dateFrom: dayjs(solution.DepartureDate).toDate(),
      packet: solution.Packet ? this.formatPacket(solution.Packet) : undefined,
      rooms: solution.Rooms_Collection?.Room ? fixArray(solution.Rooms_Collection.Room).map(e => this.formatRoom(e)) : [],
      dateTo: dayjs(solution.DepartureDate).add(solution.Nights, 'day').toDate(),
      flights: solution.Flights_Collection.Flight ? fixArray(solution.Flights_Collection.Flight).map(e => this.formatFlight(e)) : undefined,
      operatives: solution.Operatives_Collection.Operative ? fixArray(solution.Operatives_Collection.Operative).map(e => this.formatOperative(e)) : undefined,
      id: solution.Id,
      nights: solution.Nights,
      pluginSource: holiday.Plugin_Source,
      price: parseFloat(solution.MKPriceCP),
      totalPrice: parseFloat(solution.TotalPrice),
      source: solution.Source,
      hasNetQuote: !!solution.isThereNetQuote,
      hasTotalPrice: !!solution.TotalPrice,
      solutionIdOrSource
    };

    // aggiungo lo "scalo" anche nell'array "flights" (se questo esiste tra gli operatives di tipo volo)
    const flightsDepartureFromOperativeObj = detailSolutionFormatted.operatives?.find((operative) => operative.typology === OperativeTypology.VOLO && operative.type === 'ANDATA');
    const flightsReturnFromOperativeObj = detailSolutionFormatted.operatives?.find((operative) => operative.typology === OperativeTypology.VOLO && operative.type === 'RITORNO');
    if (flightsDepartureFromOperativeObj?.stopOver && flightsReturnFromOperativeObj?.stopOver && detailSolutionFormatted.flights?.length) {
      detailSolutionFormatted.flights = detailSolutionFormatted.flights.map((flight) => {
        if (flight.type === 'ANDATA') {
          flight.stopOver = flightsDepartureFromOperativeObj.stopOver;
          flight.stopOverDuration = flightsDepartureFromOperativeObj.stopOverDuration;
        }
        if (flight.type === 'RITORNO') {
          flight.stopOver = flightsReturnFromOperativeObj.stopOver;
          flight.stopOverDuration = flightsReturnFromOperativeObj.stopOverDuration;
        }
        return flight;
      });
    }

    return detailSolutionFormatted;
  }

  getSearchSolution(solutions: IHolidayDetailSolution[], searchParams: IHolidayDetailParams): IHolidayDetailSolution {
    const searchSolution = solutions.find(e => {
      return e.source === searchParams.sourceId || e.id === parseInt(searchParams.sourceId, 10);
    });
    return searchSolution ? searchSolution : this.getBestSolution(solutions, searchParams);
  }

  getBestSolution(solutions: IHolidayDetailSolution[], searchParams?: IHolidayDetailParams): IHolidayDetailSolution {
    return solutions.reduce((a, b) => {
      if (!a) {
        return b;
      }
      return b.price < a.price ? b : a;
    }, null);
  }

  getImagesFromTour(tour: IHolidayDetailTour): string[] {
    return tour.images;
  }

  getImagesFromAccomodation(accomodation: IHolidayDetailAccomodation): string[] {
    return accomodation.images;
  }

  getYouTubeCode(url: string): string {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : undefined;
  }

  getVideosFromAccomodation(accomodation: IHolidayDetailAccomodation): IHolidayDetailVideo[] {
    const videoUrl = accomodation.virtualTour;
    if (videoUrl && (videoUrl.includes('youtube') || videoUrl.includes('youtu.be'))) {
      return [{
        url: videoUrl,
        code: this.getYouTubeCode(videoUrl),
        type: VideoTypes.YOUTUBE
      }];
    }
    return [];
  }

  //  Restituisce true se la durata della soluzione coincide con quella passata come argomento o se il parametro di rotta non era stato passato ( nights === 0 )
  filterByNights(sol, nights) {
    const solNights = sol.Nights || sol.nights || 0;
    return nights ? solNights == nights : true;
  }

  // NB: "applyFilterByNights" di default è true in modo da applicare il filtro per notti dalla rotta stesso o dalla solution selezionata
  mergeAndFormatHolidays(holiday: any[], searchParams: IHolidayDetailParams, applyFilterByNights: boolean = true): IHolidayDetail {
    //applico filter su top 2005 per fix a ticket n 38384
    const refHoliday = holiday.filter(hol => hol.TourOperatorID !== 2005)[0];
    if (!refHoliday || !refHoliday?.Accomodations_Collection) {
      return null;
    }
    const accomodation = refHoliday?.Accomodations_Collection?.Accomodation ?
      this.formatAccomodation(fixArray(refHoliday?.Accomodations_Collection?.Accomodation)[0]) : undefined;
    const tour = refHoliday?.Tour ? this.formatTour(refHoliday?.Tour) : undefined;

    let solutions = [];
    if (applyFilterByNights) {
      // filtro per le notti specificate in rotta
      solutions = holiday?.flatMap(e => fixArray(e?.Solutions_Collection?.Solution)?.filter(x => x)?.filter(x => this.filterByNights(x, searchParams.duration || 0))?.map(x => this.formatSolution(x, e)));
    } else {
      // ... altrimenti, ritorno tutte le solutions disponibili
      solutions = holiday?.flatMap(e => fixArray(e?.Solutions_Collection?.Solution)?.filter(x => x)?.map(x => this.formatSolution(x, e)));
    }


    const searchSolution = (!solutions?.length || !searchParams) ? null : this.getSearchSolution(solutions, searchParams);

    //  Se il filtraggio per duration non era stato effettuato prima (perché non esisteva il parametro in rotta) viene eseguito adesos in base alla durata della searchSolution
    // NB: il filtro viene applicato sempre se "applyFilterByNights" è true altrimenti, salto questo passaggio
    if (!searchParams?.duration && applyFilterByNights) {
      solutions = solutions?.filter(x => this.filterByNights(x, searchSolution.nights));
    }

    //  Sort solutions
    solutions
      .sort((a, b) => a.totalPrice - b.totalPrice)
      .sort((a, b) => a.source === searchSolution?.source ? -1 : 1);

    const hasSolutionsWithFlight = solutions?.some(s => s.flights);
    const hasSolutionsWithNoFlight = solutions?.some(s => !s.flights);

    const useAccommodationParent = holiday.some((holiday: any) => holiday?.UseAccommodationParent);
    const accommodationParentId = accomodation?.accommodationParentId || null;

    return {
      accomodation,
      useAccommodationParent,
      accommodationParentId,
      accessories: refHoliday?.Accessories ? fixArray(refHoliday?.Accessories)?.map(e => this.formatAccessory(e)) : [],
      searchSolution: searchSolution,
      bookable: refHoliday?.Bookable,
      description: refHoliday?.Description,
      id: refHoliday?.Id,
      images: tour ? this.getImagesFromTour(tour) : this.getImagesFromAccomodation(accomodation),
      tags: tour ? tour?.tags : accomodation?.tags,
      texts: tour ? tour?.texts : accomodation?.texts,
      packet: solutions[0]?.packet,
      isPacketVisible: !!(solutions[0]?.packet &&
        solutions[0]?.packet?.title &&
        solutions[0]?.packet?.description),
      languages: tour ? tour?.languages : accomodation?.languages,
      facilities: tour ? tour?.facilities : accomodation?.facilities,
      activities: tour ? tour?.activities : accomodation?.activities,
      logo: refHoliday?.Logo,
      videos: accomodation ? this.getVideosFromAccomodation(accomodation) : [],
      isLive: refHoliday?.IsLive,
      isCustom: refHoliday?.IsCustom,
      place: this.formatPlace(fixArray(refHoliday?.Places_Collection?.Place)[0]),
      solutions,
      pluginSource: refHoliday?.Plugin_Source,
      source: refHoliday?.Source,
      title: refHoliday?.Title,
      tour,
      tourOperator: refHoliday?.TourOperator,
      tourOperatorId: refHoliday?.TourOperatorID,
      type: refHoliday?.Type,
      hasSolutionsWithFlight,
      hasSolutionsWithNoFlight,
      realTourOperatorID: refHoliday.RealTourOperatorID
    };
  }

  // getFormattedHolidayDetail(searchParams: IHolidayDetailParams, isLanding?): Observable<IHolidayDetail> {
  //   return this.getHolidayDetail(searchParams, isLanding).pipe(
  //     map(e => this.mergeAndFormatHolidays(e, searchParams)),

  getFormattedHolidayDetail(searchParams: IHolidayDetailParams, applyFilterByNights: boolean = true, isLanding?): Observable<IHolidayDetail> {
    return this.getHolidayDetail(searchParams, isLanding).pipe(
      map(e => this.mergeAndFormatHolidays(e, searchParams, applyFilterByNights)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  // Metodo utilizzato per le chiamate di dettaglio generiche, prive di parametri facoltativi
  public getHolidayGenericDetail(detailParams: HolidayDetailGenericPageSearchParams): Observable<IHolidayDetail> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap(e => {
        const baseUrl = e[0].searchUrl;
        const apiKey = e[1];
        const url = `${baseUrl}/index.php/holiday/${apiKey}/search/detail`;
        const stringParams = url + JSON.stringify(detailParams);
        if (!this.httpCache.get(stringParams)) {
          this.httpCache.set(
            stringParams,
            this._http.post<any>(url, detailParams)
              .pipe(
                map(result => this.mergeAndFormatHolidays(fixArray(result.Result.Holiday_Collection.Holiday), null, null)),
                shareReplay({bufferSize: 1, refCount: true}))
          );
        }
        return this.httpCache.get(stringParams);
      }),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getHolidayDetail(searchParams: IHolidayDetailParams, isLanding): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap(e => {
        searchParams.channel = 'B2C';
        const passengersAges = this._bookingService.getAgesString();
        const apikey = isLanding != undefined && isLanding == true ? 'eba9b9ee-e63b-4ae8-a3b2-26315e8908eb' : e[1];
        let url = `${e[0].searchUrl}/index.php/holiday/${apikey}/search/from/${searchParams.fromDate}/to/${searchParams.toDate}/accommodation/${searchParams.accomodationId}/tops/${searchParams.topId}/sourceId/${searchParams.sourceId}/plugin/${searchParams.pluginSourceId}?ages=${passengersAges}`;
        if (searchParams.onlyNetQuotes) {
          url += `&onlyNetQuotes=${searchParams.onlyNetQuotes}`;
        }
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this._http.get<any>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => fixArray(e.Result.Holiday_Collection.Holiday)),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }

  getPacketDetail(packet: string): Observable<any> {
    return forkJoin([
      this._environmentService.getEnvironment(),
      this._projectService.getAgencyApikey(),
    ]).pipe(
      mergeMap(e => {
        const url = `${e[0].otoApi2UrlDynamic}pricing/nauth/api/${e[1]}/packet/${packet}`;
        if (!this.httpCache.get(url)) {
          this.httpCache.set(url, this._http.get<any>(url).pipe(shareReplay({bufferSize: 1, refCount: true})));
        }
        return this.httpCache.get(url);
      }),
      map(e => fixArray(e.result)[0]),
      shareReplay({bufferSize: 1, refCount: true}),
      catchError(err => throwError(err))
    );
  }


  getSolutionsWithFormattedDateWithoutHours(solutions: IHolidayDetailSolution[]): any {
    return solutions.map((sol) => {
      const departureDateFormatted = dayjs(sol.dateFrom).format('YYYY-MM-DD');
      sol.dateFrom = dayjs(departureDateFormatted, 'YYYY-MM-DD').toDate();

      return sol;
    });
  }

  /**
   * Ritorna tutte le soluzioni raggruppate per la griglia solutions/prezzi
   *
   * @param solutions Array con tutte le solutions disponibili
   * @param selectedSolution Oggetto della solution selezionata (serve per verificare e rimuovere eventuali "departurePlacesId" duplicati)
   */
  getSolutionsGroupedForPriceGrid(solutions: IHolidayDetailSolution[], selectedSolution?: IHolidayDetailSolution): any {
    const solutionsGrouped = {};

    try {
      // ciclo le solutions disponibili
      for (const sol of solutions) {
        // recupero il "parentType" della solution e inizio a creare la struttura dell'oggetto per "parentType"
        const parentType = sol.parentType;
        if (!solutionsGrouped[parentType]) {
          solutionsGrouped[parentType] = {};
        }

        // recupero il mese della solution e inizio a creare la struttura dell'oggetto per "parentType" + "mese"
        const month = dayjs(sol.dateFrom).format('MMM YYYY');
        if (!solutionsGrouped[parentType][month]) {
          solutionsGrouped[parentType][month] = {};
        }

        // recupero le notti/durate della solution e inizio a creare la struttura dell'oggetto per "parentType"
        // + "mese" + "notti"
        const nights = sol.nights;
        if (!solutionsGrouped[parentType][month][nights]) {
          solutionsGrouped[parentType][month][nights] = {};
        }

        // recupero il giorno di partenza della solution e inizio a creare la struttura dell'oggetto per "parentType"
        // + "mese" + "notti" + "giorno di partenza"
        const departureDate = dayjs(sol.dateFrom).format('YYYY-MM-DD');
        if (!solutionsGrouped[parentType][month][nights][departureDate]) {
          solutionsGrouped[parentType][month][nights][departureDate] = {};
        }

        // recupero i departurePlaceId (se sono Solo Soggiorno, imposto null) della solution e inizio a creare la struttura
        // dell'oggetto per "parentType" + "mese" + "notti" + "departurePlaceId" (questo sarà un array per contenere le
        // solutions raggruppate)
        const departurePlaceId = sol.airport || null;
        if (!solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId]) {
          solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId] = [];
        }

        // pusho la solution nell'array
        solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId].push(sol);

        // ======== PARTE VERIFICA DUPLICATI "departurePlaceId" ========

        // verifico prima se l'array contiente piu di una solution e se ho passato la "selectedSolution"
        if (solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId].length > 1 && selectedSolution) {
          // salvo in una variabile a parte l'oggetto della solution selezionata nell'array raggruppato per departurePlace
          const selectedSolutionFromSolutionsGrouped =
            solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId]
              .find((solution) => solution.solutionIdOrSource === selectedSolution.solutionIdOrSource);

          // verifico se nell'array di "solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId]" è
          // presente una solution selezionata in modo da reinizializzare l'array con solo questa altrimenti, reinizializzo
          // l'array con il primo elemento (con il prezzo piu basso)
          // NB: si evita cosi di rimuovere una riga duplicata con una possibile solution selezionata
          if (selectedSolutionFromSolutionsGrouped) {
            solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId] = fixArray(selectedSolutionFromSolutionsGrouped);
          } else {
            // salvo un altro array temporaneo, le solutions con una proprietà aggiuntiva per poter verificare e
            // semplificare l'estrazione della solution con "best price" (prezzo piu basso)
            const tmpSolutionsAvailable = solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId]
              .map((solution) => {
                if (solution.totalPrice && !isNaN(solution.totalPrice)) {
                  solution.priceOrTotalPrice = solution.totalPrice;
                } else if ((!solution.totalPrice || isNaN(solution.totalPrice)) && solution.price) {
                  solution.priceOrTotalPrice = solution.price;
                }

                return solution;
              });

            // reinizializzo l'array con la sola solution "best price"
            solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId] =
              fixArray(minBy(tmpSolutionsAvailable, 'priceOrTotalPrice'));

            // rimuovo la proprietà "priceOrTotalPrice" dalla solution (serviva solo per ottenere la solution con
            // prezzo piu basso)
            delete solutionsGrouped[parentType][month][nights][departureDate][departurePlaceId][0].priceOrTotalPrice;
          }
        }

        // =============================================================
      }
    } catch (err) {
      console.error(err);
    }

    return solutionsGrouped;
  }

  /**
   * Ritorna una lista dei departurePlaces disponibili ricavata dalle solutions e raggruppati per
   * la griglia solutions/prezzi
   *
   * @param solutions Array con tutte le solutions disponibili
   */
  getDeparturePlacesGroupedForPriceGrid(solutions: IHolidayDetailSolution[]): any {
    const solutionsAirportGrouped = {};

    try {
      // ciclo le solutions disponibili
      for (const sol of solutions) {
        // recupero il "parentType" della solution e inizio a creare la struttura dell'oggetto per "parentType"
        const parentType = sol.parentType;
        if (!solutionsAirportGrouped[parentType]) {
          solutionsAirportGrouped[parentType] = {};
        }

        // recupero il mese della solution e inizio a creare la struttura dell'oggetto per "parentType" + "mese"
        const month = dayjs(sol.dateFrom).format('MMM YYYY');
        if (!solutionsAirportGrouped[parentType][month]) {
          solutionsAirportGrouped[parentType][month] = {};
        }

        // recupero le notti/durate della solution e inizio a creare la struttura dell'oggetto per "parentType"
        // + "mese" + "notti" (questo sarà un array per contenere gli id aeroporto o i null raggruppati)
        const nights = sol.nights;
        if (!solutionsAirportGrouped[parentType][month][nights]) {
          solutionsAirportGrouped[parentType][month][nights] = [];
        }

        // pusho il "departurePlaceId" nell'array (o null se sono "Solo Soggiorno")
        solutionsAirportGrouped[parentType][month][nights].push(sol.airport || null);

        // rimuovo eventuali duplicati
        solutionsAirportGrouped[parentType][month][nights] = [...new Set(solutionsAirportGrouped[parentType][month][nights])];
      }
    } catch (err) {
      console.error(err);
    }

    return solutionsAirportGrouped;
  }
}
